import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useDialog } from './DialogContext';
import axios from 'axios';
import { canManageForms } from './roles';
import { API_BASE_URL } from './config';
import { normalizeSchema } from './formSchemaUtils';
import { useFormSchema } from './useFormSchema';
import { useEntryDragReorder } from './useDragReorder';
import { checkConditions } from './formConditions';
import { flattenFields, isSection, sectionFields, sectionTitle } from './formSections';
import { cleanPresetOptions, findPresetByName, listOptionPresets } from './optionPresets';
import FieldEditorCard from './FieldEditorCard';
import InsertRow from './InsertRow';
import SectionEditorCard from './SectionEditorCard';
import SectionDeleteModal from './SectionDeleteModal';
import FormHeaderEditor from './FormHeaderEditor';
import PromptModal from './PromptModal';
import { GHOST_ICON_BTN, PRIMARY_BTN, SECONDARY_BTN } from './ui';

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const FormBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { alert, confirm } = useDialog();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [companies, setCompanies] = useState([]);
  // Only one question is open at a time: a form is easier to scan as a list of
  // titles, and the one being edited is always the one in view.
  const [expandedFieldId, setExpandedFieldId] = useState(null);
  // Set while naming a new preset: { fieldId, options }.
  const [presetPrompt, setPresetPrompt] = useState(null);
  // The section whose delete dialog is open, and the one just created — a new
  // section is unnamed, so the cursor belongs in its name box.
  const [sectionPendingDelete, setSectionPendingDelete] = useState(null);
  const [newSectionId, setNewSectionId] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const isDev = user?.role === 'dev';

  const {
    schema,
    setSchema,
    addField,
    updateField,
    updateHeader,
    addSelectOption,
    updateSelectOption,
    removeSelectOption,
    moveSelectOptionTo,
    applyOptionPreset,
    saveOptionPreset,
    deleteOptionPreset,
    addPresetOption,
    updatePresetOption,
    removePresetOption,
    removeField,
    moveEntryTo,
    stepEntryBy,
    addFieldAt,
    addFieldToSection,
    addSection,
    updateSectionName,
    setCondition,
    removeSection,
  } = useFormSchema();

  const {
    draggingId,
    canDropOn,
    entryProps,
    handleProps,
    sectionHeaderProps,
    sectionBodyProps,
    sectionEndProps,
  } = useEntryDragReorder({
    entries: schema.fields,
    onMove: moveEntryTo,
    onStep: stepEntryBy,
  });

  const presets = listOptionPresets(schema);
  // Sections group the page, not the questions themselves: calculations, report
  // numbering and validation all read the form as one list of questions.
  const allFields = flattenFields(schema.fields);
  // Every condition on the form and what, if anything, stops it working — for
  // the cards to show, and for the save to refuse while any of them is broken.
  const conditionChecks = checkConditions(schema.fields);
  const conditionCheckFor = new Map(conditionChecks.map((check) => [check.entry.id, check]));

  useEffect(() => {
    if (!user || !canManageForms(user.role)) {
      navigate('/forms');
      return;
    }

    const fetchForm = async () => {
      try {
        const requests = [axios.get(`${API_BASE_URL}/api/forms/${id}`)];
        if (user.role === 'dev') requests.push(axios.get(`${API_BASE_URL}/api/companies`));
        const [response, companiesRes] = await Promise.all(requests);
        setForm(response.data);
        if (companiesRes) setCompanies(companiesRes.data);
        const parsedSchema = normalizeSchema(JSON.parse(response.data.schema));
        setSchema(parsedSchema);
      } catch (error) {
        console.error('Failed to fetch form:', error);
        await alert({ title: 'Error', message: 'Failed to load form' });
        navigate('/forms');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [id, user, navigate, setSchema, alert]);

  // A new question opens itself and closes whichever one was open, so the
  // author can type straight into it.
  const handleAddField = () => setExpandedFieldId(addField());
  const handleAddFieldToSection = (sectionId) => setExpandedFieldId(addFieldToSection(sectionId));
  // The gaps between cards add either kind of entry. `containerId` is null for
  // a gap at the top level, or the section the gap is inside — where a new
  // section becomes a subsection.
  const handleAddFieldAt = (containerId, index) =>
    setExpandedFieldId(addFieldAt(containerId, index));
  const handleAddSection = (containerId, index) =>
    setNewSectionId(addSection(containerId, index));

  const handleRemoveField = (fieldId) => {
    removeField(fieldId);
    setExpandedFieldId((current) => (current === fieldId ? null : current));
  };

  // Deleting a section is two decisions, so it asks rather than assumes: the
  // grouping may be wrong, or the whole chapter may be.
  const handleDeleteSection = (choice) => {
    const section = sectionPendingDelete;
    setSectionPendingDelete(null);
    if (!section) return;
    removeSection(section.id, { keepFields: choice === 'sectionOnly' });
    if (choice === 'all') {
      const removed = flattenFields(sectionFields(section)).map((field) => field.id);
      setExpandedFieldId((current) => (removed.includes(current) ? null : current));
    }
  };

  const handleToggleExpanded = (fieldId) =>
    setExpandedFieldId((current) => (current === fieldId ? null : fieldId));

  const handleSavePresetRequest = (field) => {
    const options = cleanPresetOptions(field.options);
    if (options.length === 0) {
      alert({
        title: 'Nothing to save yet',
        message: 'Add at least one option before saving this dropdown as a preset.',
      });
      return;
    }
    setPresetPrompt({ fieldId: field.id, options });
  };

  const handlePresetNameConfirm = async (rawName) => {
    const name = rawName.trim();
    if (!name || !presetPrompt) return;
    const { options } = presetPrompt;
    setPresetPrompt(null);

    const existing = findPresetByName(schema, name);
    if (existing?.builtIn) {
      await alert({
        title: 'Name already in use',
        message: `“${name}” is a built-in preset. Pick a different name for this one.`,
      });
      return;
    }
    if (existing) {
      const replace = await confirm({
        title: 'Replace preset',
        message: `A preset named “${name}” already exists. Replace its options with these ${options.length}?`,
        confirmLabel: 'Replace',
      });
      if (!replace) return;
    }
    saveOptionPreset(name, options);
  };

  const handleApplyPreset = async (field, preset) => {
    const current = cleanPresetOptions(field.options);
    if (current.length > 0) {
      const replace = await confirm({
        title: 'Replace options?',
        message: `This replaces the ${current.length} option${current.length === 1 ? '' : 's'} on this question with the ${preset.options.length} from “${preset.name}”.`,
        confirmLabel: 'Replace',
        danger: true,
      });
      if (!replace) return;
    }
    applyOptionPreset(field.id, preset.options);
  };

  const handleDeletePreset = async (preset) => {
    const removed = await confirm({
      title: 'Delete preset',
      message: `Delete the “${preset.name}” preset from this form? Questions already using its options keep them.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (removed) deleteOptionPreset(preset.id);
  };

  // The name lives on the form record rather than in the schema, so renaming
  // here is a local edit like any other and lands with the next save.
  const handleRenameConfirm = (rawName) => {
    const name = rawName.trim();
    if (name) setForm((current) => ({ ...current, name }));
    setRenaming(false);
  };

  // Bring a card the author has to fix into view, once the render that opens it
  // has landed so it's scrolled to at its open height.
  const revealEntry = (entryId) => {
    window.requestAnimationFrame(() => {
      document.getElementById(`entry-${entryId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const handleSave = async () => {
    // Question text is what the inspector reads, so it is mandatory; the field
    // name is an optional short alias for reports and Excel. Questions are
    // counted across the whole form, sections or not — that is the number the
    // author sees on the card.
    const invalidIndex = allFields.findIndex((field) => !field.label?.trim());
    if (invalidIndex !== -1) {
      const message = `Question ${invalidIndex + 1} is missing its question text.`;
      setValidationError(message);
      // Open the offending question so the empty box is on screen.
      setExpandedFieldId(allFields[invalidIndex].id);
      revealEntry(allFields[invalidIndex].id);
      alert({ title: 'Missing question text', message });
      return;
    }
    // A condition left unfinished, or broken by an edit elsewhere — its
    // question deleted, the option it chose renamed away — would be ignored
    // on the live form, showing a question the author meant to hide. Better
    // to say so now, at the entry it's on.
    const brokenCondition = conditionChecks.find((check) => check.problem);
    if (brokenCondition) {
      const { entry, problem } = brokenCondition;
      const where = isSection(entry)
        ? `the “${sectionTitle(entry)}” section`
        : `question ${allFields.indexOf(entry) + 1}`;
      const message = `Check the condition on ${where}. ${problem.message}`;
      setValidationError(message);
      if (!isSection(entry)) setExpandedFieldId(entry.id);
      revealEntry(entry.id);
      alert({ title: 'Condition needs attention', message });
      return;
    }
    setValidationError('');
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/forms/${id}`, {
        name: form.name,
        description: form.description,
        schema: JSON.stringify(schema),
        ...(isDev ? { company_id: form.company_id } : {}),
      });
      await alert({ title: 'Success', message: 'Form saved successfully!' });
      navigate('/forms');
    } catch (error) {
      console.error('Failed to save form:', error);
      alert({
        title: 'Error',
        message: error.response?.data?.msg || 'Failed to save form',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">Loading form...</div>
        </div>
      </div>
    );
  }

  if (!form) return null;

  const renderField = (field) => (
    <FieldEditorCard
      key={field.id}
      field={field}
      index={allFields.indexOf(field)}
      fields={allFields}
      formEntries={schema.fields}
      conditionCheck={conditionCheckFor.get(field.id)}
      setCondition={setCondition}
      expanded={expandedFieldId === field.id}
      onToggleExpanded={() => handleToggleExpanded(field.id)}
      isDragging={draggingId === field.id}
      itemProps={entryProps(field.id)}
      handleProps={handleProps(field.id)}
      presets={presets}
      updateField={updateField}
      removeField={handleRemoveField}
      addSelectOption={addSelectOption}
      updateSelectOption={updateSelectOption}
      removeSelectOption={removeSelectOption}
      moveSelectOptionTo={moveSelectOptionTo}
      onApplyPreset={handleApplyPreset}
      onSavePreset={handleSavePresetRequest}
      onDeletePreset={handleDeletePreset}
      addPresetOption={addPresetOption}
      updatePresetOption={updatePresetOption}
      removePresetOption={removePresetOption}
      alert={alert}
    />
  );

  const renderSection = (section, depth) => (
    <SectionEditorCard
      key={section.id}
      section={section}
      depth={depth}
      autoFocus={newSectionId === section.id}
      isDragging={draggingId === section.id}
      dragActive={canDropOn(section.id)}
      entryProps={entryProps(section.id)}
      handleProps={handleProps(section.id)}
      headerProps={sectionHeaderProps(section.id)}
      bodyProps={sectionBodyProps(section.id)}
      endProps={sectionEndProps(section.id)}
      formEntries={schema.fields}
      conditionCheck={conditionCheckFor.get(section.id)}
      setCondition={setCondition}
      updateSectionName={updateSectionName}
      removeSection={setSectionPendingDelete}
      addFieldToSection={handleAddFieldToSection}
      addFieldAt={handleAddFieldAt}
      addSection={handleAddSection}
      renderEntry={renderEntry}
    />
  );

  // Sections draw what they hold through this, so a subsection comes back
  // round as a section one level deeper.
  const renderEntry = (entry, depth = 0) =>
    (isSection(entry) ? renderSection(entry, depth) : renderField(entry));

  const saveButton = (
    <button
      onClick={handleSave}
      disabled={saving}
      className={`${PRIMARY_BTN} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {saving ? 'Saving...' : 'Save Form'}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
      <PromptModal
        open={!!presetPrompt}
        title="Save as preset"
        message="Name this set of options so you can reuse it on other questions in this form."
        placeholder="e.g. Pass / Fail / N/A"
        confirmLabel="Save preset"
        required
        onConfirm={handlePresetNameConfirm}
        onCancel={() => setPresetPrompt(null)}
      />
      <SectionDeleteModal
        open={!!sectionPendingDelete}
        section={sectionPendingDelete}
        onChoose={handleDeleteSection}
        onCancel={() => setSectionPendingDelete(null)}
      />
      <PromptModal
        open={renaming}
        title="Rename form"
        message="This is the name the form is listed under. The new name is stored when you save the form."
        defaultValue={form.name}
        placeholder="Form name"
        confirmLabel="Rename"
        required
        onConfirm={handleRenameConfirm}
        onCancel={() => setRenaming(false)}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold text-white mb-2">Form Builder</h1>
              <div className="flex items-center gap-2">
                <p className="text-lg text-gray-400 truncate">Edit form: {form.name}</p>
                <button
                  type="button"
                  onClick={() => setRenaming(true)}
                  className={`${GHOST_ICON_BTN} shrink-0`}
                  title="Rename this form"
                  aria-label="Rename this form"
                >
                  <PencilIcon />
                </button>
              </div>
            </div>
            <div className="flex space-x-4 shrink-0">
              <button onClick={() => navigate('/forms')} className={SECONDARY_BTN}>
                Cancel
              </button>
              {saveButton}
            </div>
          </div>
        </div>

        {isDev && (
          <div className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 mb-6">
            <label htmlFor="fb-company" className="block text-sm font-medium text-gray-300 mb-2">
              Company
            </label>
            <select
              id="fb-company"
              value={form.company_id || ''}
              onChange={(e) => setForm({ ...form, company_id: Number(e.target.value) || null })}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
            >
              <option value="" disabled>Select a company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <FormHeaderEditor header={schema.header} updateHeader={updateHeader} />

        {validationError && (
          <div className="mb-4 rounded-lg border border-red-500 bg-red-500/10 p-4 text-red-200">
            {validationError}
          </div>
        )}

        {/* Form Fields */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-6">Questions</h2>

          {schema.fields.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-4">No questions yet. Add your first question to get started.</p>
              <button onClick={handleAddField} className={PRIMARY_BTN}>
                Add First Question
              </button>
            </div>
          ) : (
            <>
              {/* One list, however many levels deep: a section draws what it
                  holds nested inside it, subsections included. The gaps carry
                  the insert controls at every level, so a question or a
                  section can be started anywhere a card can end — including
                  above the first question and below the last. */}
              <div>
                <InsertRow
                  onAddQuestion={() => handleAddFieldAt(null, 0)}
                  onAddSection={() => handleAddSection(null, 0)}
                  place="here, above the first question"
                />
                {schema.fields.map((entry, index) => (
                  <React.Fragment key={entry.id}>
                    {renderEntry(entry)}
                    <InsertRow
                      onAddQuestion={() => handleAddFieldAt(null, index + 1)}
                      onAddSection={() => handleAddSection(null, index + 1)}
                      place="here"
                    />
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-2">
                <button onClick={handleAddField} className={PRIMARY_BTN}>
                  Add Question
                </button>
              </div>
            </>
          )}
        </div>

        {/* Repeated at the foot of the page: a long form is a long scroll back
            to the button in the header. */}
        <div className="flex justify-end">{saveButton}</div>
      </div>
    </div>
  );
};

export default FormBuilder;
